/**
 * ATW — Search assistant (προθέματα, τυπογραφικά, ελληνικά ↔ λατινικά)
 *
 * ΤΙ ΚΑΝΕΙ
 * Η Shopify ταιριάζει τους όρους των Product boosts μόνο ολόκληρους (Armani, όχι Arm).
 * Εδώ χτίζουμε ένα λεξιλόγιο από όσα ήδη συντηρείς στο Search & Discovery
 * (ονόματα, όροι Product boosts, προαιρετικά tags) και, πριν σταλεί η αναζήτηση,
 * ο ημιτελής όρος συμπληρώνεται σε πλήρεις λέξεις:  "arm" → "armani".
 * Το αποτέλεσμα το βρίσκει η ίδια η Shopify· άρα φίλτρα, σελιδοποίηση και ταξινόμηση
 * παραμένουν native. Δεν υπάρχει δεύτερη λίστα όρων που πρέπει να συντηρείς.
 *
 * ΠΗΓΗ ΔΕΔΟΜΕΝΩΝ: /collections/all?view=atw-search-index (templates/collection.atw-search-index.liquid)
 * ΡΥΘΜΙΣΕΙΣ: Theme settings → ATW — Search — Discovery → «Έξυπνη αναζήτηση».
 */
(() => {
  if (window.AtwSearch) return;

  const cfgNode = document.getElementById('atw-search-config');
  let cfg = { enabled: true, max: 6, minChars: 2, tags: true, fuzzy: true, notes: true };
  try {
    if (cfgNode) cfg = { ...cfg, ...JSON.parse(cfgNode.textContent || '{}') };
  } catch (_) {
    /* default ρυθμίσεις */
  }

  const CACHE_KEY = 'atw-sx-v4';
  const CACHE_TTL = 30 * 60 * 1000;
  const MAX_TERMS = 3;
  /** Μέγιστος αριθμός προϊόντων που στοχεύουμε μέσω νότας· πάνω από αυτό η νότα είναι πολύ γενική και αφήνουμε τη native αναζήτηση. */
  const NOTE_CAP = 80;
  const STOP = new Set(['και', 'για', 'στο', 'στη', 'στην', 'στον', 'απο', 'της', 'των', 'του', 'την', 'τον', 'ενα', 'μια', 'που', 'πιο', 'με', 'σε', 'the', 'and', 'of']);

  /** @type {{o:string,k:string,f:string,df:number,s:number[]}[] | null} */
  let vocab = null;
  /** Λεξιλόγιο νοτών / κατευθύνσεων / χώρων (ίδια μορφή με το vocab). */
  let nvocab = null;
  /** Τίτλοι προϊόντων ανά θέση του ευρετηρίου, για στόχευση με title:"…". */
  let titles = [];
  /** @type {Promise<void> | null} */
  let loading = null;

  /* ---------- κανονικοποίηση ---------- */

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/ς/g, 'σ')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();

  const GREEK = {
    α: 'a', β: 'v', γ: 'g', δ: 'd', ε: 'e', ζ: 'z', η: 'i', θ: 't', ι: 'i', κ: 'k', λ: 'l', μ: 'm',
    ν: 'n', ξ: 'ks', ο: 'o', π: 'p', ρ: 'r', σ: 's', τ: 't', υ: 'i', φ: 'f', χ: 'h', ψ: 'ps', ω: 'o',
  };

  const isGreekWord = (t) => /[α-ω]/.test(t);

  /** Φωνητικό κλειδί: ίδιο για «Αρμάνι» και «Armani». */
  const fold = (t) => {
    let s = t;
    if (/[α-ω]/.test(s)) {
      s = s
        .replace(/ου/g, 'u').replace(/αι/g, 'e').replace(/ει/g, 'i').replace(/οι/g, 'i')
        .replace(/μπ/g, 'b').replace(/ντ/g, 'd').replace(/γκ/g, 'g').replace(/γγ/g, 'g')
        .replace(/τσ/g, 'ts').replace(/τζ/g, 'dz')
        .replace(/[α-ω]/g, (c) => GREEK[c] || c);
    } else {
      s = s
        .replace(/ou/g, 'u').replace(/ai/g, 'e').replace(/ei/g, 'i').replace(/oi/g, 'i')
        .replace(/th/g, 't').replace(/ph/g, 'f').replace(/ch/g, 'h')
        .replace(/y/g, 'i').replace(/w/g, 'v').replace(/q/g, 'k').replace(/x/g, 'ks')
        .replace(/c(?=[ei])/g, 's').replace(/c/g, 'k').replace(/j/g, 'g');
    }
    return s.replace(/(.)\1+/g, '$1');
  };

  /** true αν η απόσταση Damerau-Levenshtein είναι ≤ 1 */
  const within1 = (a, b) => {
    if (a === b) return true;
    const la = a.length;
    const lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    let i = 0;
    while (i < la && i < lb && a[i] === b[i]) i++;
    if (la === lb) {
      if (a.slice(i + 1) === b.slice(i + 1)) return true;
      return a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2);
    }
    return la > lb ? a.slice(i + 1) === b.slice(i) : b.slice(i + 1) === a.slice(i);
  };


  /** Απόσταση Damerau-Levenshtein (OSA) για μικρές λέξεις. */
  const osa = (a, b) => {
    const la = a.length;
    const lb = b.length;
    const d = [];
    for (let i = 0; i <= la; i++) {
      d[i] = [i];
    }
    for (let j = 1; j <= lb; j++) d[0][j] = j;
    for (let i = 1; i <= la; i++) {
      for (let j = 1; j <= lb; j++) {
        const c = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + c);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
    }
    return d[la][lb];
  };

  /** Σκελετός συμφώνων: «gotier» → «gtr», «gaultier» → «gltr». */
  const skeleton = (f) => f.charAt(0) + f.slice(1).replace(/[aeiou]/g, '');

  const commonPrefix = (a, b) => {
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    return i;
  };
  const commonSuffix = (a, b) => {
    let i = 0;
    while (i < a.length && i < b.length && a[a.length - 1 - i] === b[b.length - 1 - i]) i++;
    return i;
  };

  /** Πόσα γράμματα λάθος ανέχομαστε, ανάλογα με το μήκος της λέξης. */
  const allowedEdits = (len) => (len >= 11 ? 3 : len >= 7 ? 2 : len >= 4 ? 1 : 0);

  /**
   * Ορθογραφικό ή φωνητικό «κοντινό» ολόκληρης λέξης (π.χ. channel→chanel, gotier→gaultier).
   * Επιστρέφει την απόσταση (μικρότερη = καλύτερη) ή -1 αν δεν είναι κοντινή.
   */
  const nearDistance = (q, qf, e, e0) => {
    const w = e.k;
    if (w.length < 4 || e.f.charAt(0) !== qf.charAt(0)) return -1;
    if (Math.abs(w.length - q.length) > 3) return -1;
    const len = Math.max(qf.length, e.f.length);
    const dRaw = osa(q, w);
    if (dRaw <= (q.length >= 8 ? 2 : 1)) return dRaw;
    const d = osa(qf, e.f);
    if (e0 && d > 1) return -1; // η λέξη υπάρχει ήδη: ανέχομαστε μόνο παραλλαγή 1 γράμματος
    const edge = commonPrefix(qf, e.f) + commonSuffix(qf, e.f);
    if (d <= allowedEdits(len) && edge >= Math.min(4, len - 2)) return d;
    // Φωνητικό: διαφορά μόνο σε φωνήεντα/ένα σύμφωνο (gotier ↔ gaultier)
    const ws = skeleton(e.f);
    const qs = skeleton(qf);
    if (qf.length >= 6 && qs.length >= 3 && ws.length >= 3 && osa(qs, ws) <= 1 && d <= 3 && edge >= 5) return d;
    return -1;
  };

  /** Πατήθηκε ελληνικό πληκτρολόγιο αντί για λατινικό: «ψηανελ» → «chanel». */
  const KEYS = {
    ε: 'e', ρ: 'r', τ: 't', υ: 'y', θ: 'u', ι: 'i', ο: 'o', π: 'p', α: 'a', σ: 's', δ: 'd', φ: 'f', γ: 'g',
    η: 'h', ξ: 'j', κ: 'k', λ: 'l', ζ: 'z', χ: 'x', ψ: 'c', ω: 'v', β: 'b', ν: 'n', μ: 'm',
  };
  const fromGreekKeys = (t) => t.replace(/[α-ω]/g, (c) => KEYS[c] || c);

  /* ---------- λεξιλόγιο ---------- */

  const build = (items) => {
    const map = new Map();
    const nmap = new Map();
    let cur = 0;
    const adder = (target, minLen, stop) => (text, seen) => {
      String(text || '')
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .forEach((orig) => {
          const k = norm(orig);
          if (k.length < minLen || seen.has(k) || (stop && STOP.has(k))) return;
          seen.add(k);
          const e = target.get(k);
          if (e) {
            e.df += 1;
            e.s.push(cur);
          } else target.set(k, { o: orig, k, f: fold(k), df: 1, s: [cur] });
        });
    };
    const add = adder(map, 2, false);
    const addNote = adder(nmap, 3, true);
    items.forEach((it, idx) => {
      const seen = new Set();
      const nseen = new Set();
      cur = idx;
      add(it.n, seen);
      (it.b || []).forEach((b) => add(b, seen));
      if (cfg.tags) {
        (it.g || []).forEach((g) => add(g, seen));
        add(it.p, seen);
      }
      (it.k || []).forEach((k) => addNote(k, nseen));
    });
    return { v: [...map.values()], nv: [...nmap.values()], T: items.map((it) => it.n || '') };
  };

  const indexUrl = (page) => {
    const base = (window.Theme?.routes?.search_url || '/search').replace(/\/search\/?$/, '') + '/collections/all';
    return `${base}?view=atw-search-index${page > 1 ? `&page=${page}` : ''}`;
  };

  const load = () => {
    if (vocab) return Promise.resolve();
    if (loading) return loading;

    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < CACHE_TTL && cached.tags === cfg.tags) {
        vocab = cached.v;
        nvocab = cached.nv || [];
        titles = cached.T || [];
        return Promise.resolve();
      }
    } catch (_) {
      /* χωρίς cache */
    }

    loading = (async () => {
      const items = [];
      let page = 1;
      let pages = 1;
      do {
        const res = await fetch(indexUrl(page), { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`index ${res.status}`);
        const json = await res.json();
        items.push(...json.items);
        pages = json.pages || 1;
        page += 1;
      } while (page <= pages && page <= 8);

      const built = build(items);
      vocab = built.v;
      nvocab = built.nv;
      titles = built.T;
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), tags: cfg.tags, v: vocab, nv: nvocab, T: titles }));
      } catch (_) {
        /* γεμάτο storage: συνεχίζουμε χωρίς cache */
      }
    })()
      .catch(() => {
        vocab = null;
      })
      .finally(() => {
        loading = null;
      });

    return loading;
  };

  /* ---------- επέκταση όρου ---------- */

  /** Βρίσκει υποψήφιες λέξεις του λεξιλογίου για έναν (ημιτελή ή λάθος) όρο. */
  const resolve = (q, V = vocab, canJoin = true) => {
    const qf = fold(q);
    const hits = [];
    for (const e of V) {
      if (e.k.startsWith(q)) hits.push([0, e]);
      else if (isGreekWord(q) !== isGreekWord(e.k) && e.f.startsWith(qf)) hits.push([1, e]);
    }

    // Ενωμένες λέξεις: «tomford» → «tom ford»
    if (canJoin && q.length >= 6 && cfg.fuzzy && !V.some((e) => e.k === q)) {
      let best = null;
      for (let i = 3; i <= q.length - 3; i++) {
        const l = V.find((e) => e.k === q.slice(0, i));
        const r = V.find((e) => e.k === q.slice(i));
        if (l && r && (!best || l.df + r.df > best.df)) best = { df: l.df + r.df, text: `${l.o} ${r.o}` };
      }
      if (best) return { joined: best.text, hits };
    }

    // Λάθη πληκτρολόγησης / ορθογραφίας σε ολόκληρη λέξη. Δεχόμαστε τη «γειτονική» λέξη μόνο
    // αν είναι τουλάχιστον το ίδιο συχνή με αυτό που γράφτηκε (π.χ. channel → chanel).
    // Για λέξεις 4 γραμμάτων μόνο όταν δεν υπάρχει άλλη αντιστοιχία.
    const near = [];
    if (cfg.fuzzy && (q.length >= 5 || (q.length === 4 && !hits.length))) {
      const exact = V.find((e) => e.k === q);
      const exactDf = exact ? exact.df : 0;
      const seenHit = new Set(hits.map((h) => h[1].k));
      for (const e of V) {
        if (seenHit.has(e.k) || e.df < exactDf || e.k === q) continue;
        const d = nearDistance(q, qf, e, exact);
        if (d >= 0) near.push([3 + d / 10, e]);
      }
    }
    if (!hits.length && !near.length && cfg.fuzzy && q.length >= 4) {
      for (const e of V) {
        if (within1(q, e.k.slice(0, q.length)) || within1(q, e.k.slice(0, q.length - 1))) hits.push([2, e]);
      }
    }
    return { hits: hits.concat(near) };
  };

  /** Συμπληρώνει την τελευταία λέξη· επιστρέφει το ίδιο κείμενο αν δεν χρειάζεται αλλαγή. */
  const expandNames = (term) => {
    const raw = String(term || '').trim();
    if (!cfg.enabled || !vocab || !raw || /[:"()]/.test(raw)) return raw;

    const parts = raw.split(/\s+/);
    const last = parts.pop() || '';
    const prefix = parts.join(' ');
    const q = norm(last);
    if (q.length < cfg.minChars || q.includes(' ')) return raw;
    const withPrefix = (t) => (prefix ? `${prefix} ${t}` : t);

    let r = resolve(q);
    if (r.joined) return withPrefix(r.joined);

    // Τίποτα δεν βρέθηκε και το έγραψε με ελληνικό πληκτρολόγιο: δοκιμάζουμε τα ίδια πλήκτρα σε λατινικά
    if (!r.hits.length && isGreekWord(q) && q.length >= 3) {
      const swapped = fromGreekKeys(q);
      if (swapped !== q) {
        const r2 = resolve(swapped);
        if (r2.joined) return withPrefix(r2.joined);
        if (r2.hits.length) r = r2;
      }
    }
    const hits = r.hits;
    if (!hits.length) return raw;

    hits.sort((a, b) => a[0] - b[0] || b[1].df - a[1].df || a[1].k.length - b[1].k.length);
    // Κρατάμε μόνο λέξεις που προσθέτουν προϊόντα που δεν καλύπτονται ήδη
    // (π.χ. το «chanel» καλύπτει τα «channel», «chanelbleu»…). Λίγοι, ουσιαστικοί όροι:
    // η Shopify δίνει λιγότερα αποτελέσματα όταν το OR έχει πολλούς όρους.
    const limit = Math.max(1, Math.min(cfg.max, MAX_TERMS));
    const picked = [];
    const covered = new Set();
    for (const [, e] of hits) {
      if (picked.length >= limit) break;
      if (!e.s.some((i) => !covered.has(i))) continue;
      picked.push(e.o);
      e.s.forEach((i) => covered.add(i));
    }

    // Αν η μόνη συμπλήρωση είναι ο ίδιος ο όρος, δεν αλλάζουμε τίποτα.
    if (picked.length === 1 && norm(picked[0]) === q) return raw;

    return picked.map(withPrefix).join(' OR ');
  };

  /**
   * Νότες, κατευθύνσεις αρώματος και χώροι: ο όρος αντιστοιχίζεται (με πρόθεμα, ορθογραφικά, ελληνικά ↔ λατινικά)
   * σε προϊόντα που τον έχουν στα δεδομένα τους και στοχεύονται με title:"…".
   * Αφορά μόνο μονολεκτικές αναζητήσεις· αν η νότα είναι πολύ γενική (πάνω από NOTE_CAP προϊόντα) δεν προστίθεται τίποτα.
   */
  const noteClauses = (q) => {
    if (!cfg.notes || !nvocab || !nvocab.length || q.length < 3) return [];
    let r = resolve(q, nvocab, false);
    if (!r.hits.length && isGreekWord(q)) {
      const swapped = fromGreekKeys(q);
      if (swapped !== q) r = resolve(swapped, nvocab, false);
    }
    if (!r.hits.length) return [];
    const exact = r.hits.filter((h) => h[1].k === q);
    let use = exact;
    if (!use.length) {
      const best = Math.min(...r.hits.map((h) => h[0]));
      use = r.hits.filter((h) => h[0] === best);
    }
    const ids = new Set();
    use.forEach(([, e]) => e.s.forEach((i) => ids.add(i)));
    if (!ids.size || ids.size > NOTE_CAP) return [];
    const uniq = new Set(
      [...ids].map((i) => String(titles[i] || '').replace(/["()]/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean)
    );
    return [...uniq].map((t) => `title:"${t}"`);
  };

  const expandSync = (term, opts) => {
    const raw = String(term || '').trim();
    const out = expandNames(raw);
    if (!cfg.enabled || !vocab || !raw || /[:"()]/.test(raw) || /\s/.test(raw)) return out;
    if (!opts || opts.notes !== true) return out;
    const clauses = noteClauses(norm(raw));
    return clauses.length ? [out, ...clauses].join(' OR ') : out;
  };

  /** Περιμένει (μέχρι 700ms) το λεξιλόγιο και επιστρέφει τον εμπλουτισμένο όρο. */
  const expand = async (term, opts) => {
    if (!cfg.enabled) return String(term || '').trim();
    if (!vocab) {
      await Promise.race([load(), new Promise((r) => setTimeout(r, 700))]);
    }
    return expandSync(term, opts);
  };

  /** URL σελίδας αποτελεσμάτων: το q είναι ο εμπλουτισμένος όρος, το atw_q αυτό που έγραψε ο χρήστης. */
  const searchUrl = async (typed, extra) => {
    const t = String(typed || '').trim();
    const url = new URL(window.Theme?.routes?.search_url || '/search', location.origin);
    const expanded = await expand(t);
    url.searchParams.set('q', expanded);
    url.searchParams.set('type', 'product');
    if (expanded !== t) url.searchParams.set('atw_q', t);
    Object.entries(extra || {}).forEach(([k, v]) => url.searchParams.set(k, v));
    return url.toString();
  };

  /* ---------- φόρμες αναζήτησης (modal, σελίδα αποτελεσμάτων) ---------- */

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !cfg.enabled) return;
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const searchPath = new URL(window.Theme?.routes?.search_url || '/search', location.origin).pathname;
      if (new URL(form.action, location.origin).pathname !== searchPath) return;
      if (form.dataset.atwSearchPassed === '1') return;

      const typed = input.value.trim();
      if (!typed || !vocab) return; // χωρίς λεξιλόγιο: native αναζήτηση

      const expanded = expandSync(typed);
      if (expanded === typed) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const data = new FormData(form);
      data.set('q', expanded);
      data.set('atw_q', typed);
      const url = new URL(form.action, location.origin);
      data.forEach((v, k) => url.searchParams.set(k, String(v)));
      window.location.href = url.toString();
    },
    true
  );

  /* ---------- σελίδα αποτελεσμάτων: εμφάνιση αυτού που έγραψε ο χρήστης ---------- */

  const restoreTyped = () => {
    const params = new URLSearchParams(location.search);
    const typed = params.get('atw_q');
    const used = params.get('q');
    if (!typed || !used) return;
    document.querySelectorAll('input.search-page-input, input[data-atw-search-input]').forEach((el) => {
      if (el instanceof HTMLInputElement && el.value === used) el.value = typed;
    });
    document.querySelectorAll('[data-atw-search-terms]').forEach((el) => {
      el.textContent = typed;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreTyped);
  else restoreTyped();

  /* ---------- φόρτωση λεξιλογίου όταν ο χρήστης πάει να ψάξει ---------- */

  const warm = () => {
    if (cfg.enabled) load();
  };
  document.addEventListener(
    'focusin',
    (event) => {
      const el = event.target;
      if (el instanceof HTMLInputElement && el.name === 'q') warm();
    },
    { passive: true }
  );
  document.addEventListener('pointerover', (event) => {
    const el = event.target;
    if (el instanceof Element && el.closest('[aria-controls="search-modal"], [data-search-trigger], [data-atw-open-search], .header-actions__action[href*="search"]')) warm();
  }, { passive: true, once: true });
  if (cfg.enabled && sessionStorage.getItem(CACHE_KEY)) load(); // ήδη στο cache: φορτώνει αμέσως, χωρίς δίκτυο

  /* ---------- dropdown προτάσεων: συμπλήρωση του όρου μέσα στο αίτημα ---------- */

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    try {
      const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
      if (cfg.enabled && href && /section_id=atw-predictive-search/.test(href)) {
        const url = new URL(href, location.origin);
        const predictivePath = new URL(window.Theme?.routes?.predictive_search_url || '/search/suggest', location.origin).pathname;
        const q = url.searchParams.get('q');
        if (url.pathname === predictivePath && q && !/[:"()]/.test(q)) {
          const expanded = await expand(q, { notes: true });
          if (expanded !== q.trim()) {
            url.searchParams.set('q', expanded);
            return nativeFetch(url.toString(), init);
          }
        }
      }
    } catch (_) {
      /* σε οποιοδήποτε πρόβλημα: native αίτημα */
    }
    return nativeFetch(input, init);
  };

  /* Enter στο πεδίο του modal (χωρίς επιλεγμένο αποτέλεσμα): ίδια συμπλήρωση και στη σελίδα αποτελεσμάτων */
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' || !cfg.enabled || !vocab) return;
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
      const root = input.closest('predictive-search-component');
      if (!root || root.querySelector('[aria-selected="true"], [data-single-result-url]')) return;
      const typed = input.value.trim();
      if (!typed) return;
      const expanded = expandSync(typed);
      if (expanded === typed) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const url = new URL(window.Theme?.routes?.search_url || '/search', location.origin);
      url.searchParams.set('q', expanded);
      url.searchParams.set('type', 'product');
      url.searchParams.set('atw_q', typed);
      window.location.href = url.toString();
    },
    true
  );

  /* ---------- σελίδα αποτελεσμάτων: λίγα αποτελέσματα → προσθήκη προϊόντων με την ίδια νότα / κατεύθυνση / χώρο ---------- */

  /**
   * Η native αναζήτηση της Shopify είναι ήδη καλή όταν βρίσκει αρκετά προϊόντα. Όταν βρίσκει λίγα (π.χ. «caramel» → 1),
   * ξανασχεδιάζουμε το αίτημα ώστε να περιλαμβάνει και προϊόντα που έχουν τη νότα στα δεδομένα τους.
   * Γίνεται μία φορά (παράμετρος atw_n) και μόνο για μονολεκτικούς όρους.
   */
  const refine = async () => {
    if (!cfg.enabled || !cfg.notes) return;
    const holder = document.querySelector('[data-atw-count]');
    if (!holder) return;
    const params = new URLSearchParams(location.search);
    if (params.get('atw_n')) return;
    const typed = String(params.get('atw_q') || params.get('q') || '').trim();
    if (!typed || /[:"()]/.test(typed) || /\s/.test(typed)) return;
    const count = parseInt(holder.getAttribute('data-atw-count') || '', 10);
    const below = parseInt(holder.getAttribute('data-atw-below') || '12', 10);
    if (Number.isNaN(count) || count >= below) return;
    await load();
    const next = expandSync(typed, { notes: true });
    if (!next || next === (params.get('q') || '').trim()) return;
    params.set('q', next);
    params.set('atw_q', typed);
    params.set('atw_n', '1');
    location.replace(`${location.pathname}?${params.toString()}`);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refine);
  else refine();

  window.AtwSearch = { expand, expandSync, searchUrl, load, norm, fold };
})();
